/**
 * A market that is over. The other half of `/trade/:asset?anchor=`.
 *
 * **This is what a History deep link opens**, and building it is what made
 * Phase 8's fifth exit criterion mean anything: the anchor was declared the
 * canonical deep link in `router.tsx` from Phase 3, and until §F28 nothing read
 * it except to preserve it across an asset switch — so a link into a settled
 * market rendered the *live* one, convincingly.
 *
 * **One request, and on the second visit not even that.** The origin serves a
 * settled market `max-age=31536000, immutable` once
 * `resolution.corrections_closed` is true, and `api/http/cache.ts` honours what
 * it is told. That is the whole of "renders from cache on the second visit", and
 * at 2,880 markets a day it is what makes History affordable at all (D4).
 *
 * **Nothing is subscribed.** There is no book past `SETTLED` — §7.6 drops the
 * ladder and the endpoint answers `200` with `final: true` — no ticker, no
 * countdown, and nothing to keep current. A screen that subscribed anyway would
 * be paying a channel out of the connection's two hundred to render a number
 * that cannot change.
 *
 * **`corrections_closed` is rendered, not inferred.** While it is `false` a late
 * `RESOLVING_DEGRADED` correction can still move a number on this page, and the
 * page says so. A client deriving the window from `settled_at_ms` plus ten
 * seconds would be inventing a timing constant, which is invariant 11's subject
 * and has cost this repository five defects.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Panel } from '@/components/layout'
import { EmptyState, Glyph, Pill, Skeleton } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatBps, formatPriceString, formatUtcTime } from '@/lib/format'
import { useRuntime, useRuntimeState } from '@/app/use-runtime'
import { parseMarketId } from '@/lib/market-id'

export interface ArchivedMarketProps {
  marketId: string
  className?: string
}

type Market = Record<string, unknown>

export function ArchivedMarket({ marketId, className }: ArchivedMarketProps) {
  const runtime = useRuntime()
  const { assets } = useRuntimeState()

  const { data, isPending, isError } = useQuery({
    queryKey: ['market', marketId],
    queryFn: () => runtime.api.markets.getMarket(marketId) as Promise<Market>,
    // The HTTP layer already holds the origin's policy; react-query holding a
    // second, shorter one would re-ask for something the client knows can never
    // change. Two caches with two opinions is one cache too many.
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const { asset, anchorS } = parseMarketId(marketId)
  const resolution = (data?.resolution ?? null) as Record<string, unknown> | null
  const outcome = typeof resolution?.outcome === 'string' ? resolution.outcome : null
  const closed = resolution?.corrections_closed === true

  return (
    <div
      className={cx('ud-scroll flex min-h-0 flex-1 flex-col gap-px bg-bg-canvas', className)}
      data-testid="archived-market"
      data-market-id={marketId}
    >
      <div className="mx-auto w-full max-w-[calc(1120*var(--ud-u))]">
        <Panel header={`settled · ${marketId}`}>
          {isPending ?
            <Skeleton className="h-40 w-full" />
          : isError || !data ?
            <EmptyState>This market could not be loaded.</EmptyState>
          : <div className="flex flex-col gap-3 px-3 py-3">
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-title text-text-primary">{String(data.title ?? marketId)}</span>
                <Pill tone={outcome === 'UP' ? 'up' : outcome === 'DOWN' ? 'down' : 'neutral'}>
                  {outcome ? (
                    <span className="flex items-center gap-1">
                      <Glyph name={outcome === 'UP' ? 'up' : 'down'} label={null} />
                      {outcome}
                    </span>
                  ) : (
                    String(data.state ?? '—')
                  )}
                </Pill>
              </div>

              <dl className="flex flex-wrap gap-x-8 gap-y-2">
                <Figure label="Strike" value={priceOf(resolution?.strike_price)} />
                <Figure label="Settlement" value={priceOf(resolution?.settlement_price)} />
                {/*
                  `formatBps`, not a hand-rolled 2dp. `delta_bps` is a 4dp field
                  and `lib/decimal` refuses to truncate money-shaped values —
                  "truncating here would lose money that the ledger will later
                  ask for" — so passing the wrong scale throws rather than
                  rounding, which is how this was caught.
                */}
                <Figure
                  label="Delta"
                  value={typeof resolution?.delta_bps === 'string' ? formatBps(resolution.delta_bps) : null}
                />
                <Figure
                  label="Settled"
                  value={
                    typeof resolution?.settled_at_ms === 'number' ?
                      formatUtcTime(resolution.settled_at_ms)
                    : null
                  }
                />
              </dl>

              {/*
                Said out loud, because it is the difference between a number that
                is final and a number that is merely current. The origin will not
                serve this market `immutable` until the same flag is true, so the
                page and the cache agree by construction rather than by comment.
              */}
              <p className="text-caption text-text-tertiary">
                {closed ?
                  'Final. No number on this market can change again.'
                : 'Provisional — a late correction can still change these numbers.'}
              </p>

              <div className="flex flex-wrap gap-3 text-body-sm">
                <Link className="text-text-link hover:underline" to={`/trade/${asset}`}>
                  Go to the live {asset.toUpperCase()} market
                </Link>
                {assets
                  .filter((sibling) => sibling !== asset)
                  .map((sibling) => (
                    <Link
                      key={sibling}
                      className="text-text-link hover:underline"
                      to={`/trade/${sibling}?anchor=${anchorS}`}
                    >
                      The {sibling.toUpperCase()} market at this anchor
                    </Link>
                  ))}
                <Link className="text-text-link hover:underline" to="/history">
                  Back to History
                </Link>
              </div>
            </div>
          }
        </Panel>
      </div>
    </div>
  )
}

function Figure({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-0_5">
      <dt className="text-caption text-text-tertiary">{label}</dt>
      <dd className="ud-num text-body text-text-primary">{value ?? '—'}</dd>
    </div>
  )
}

/**
 * A `Price` renders from its **decimal string**, never from `raw` and `expo`.
 * The pair is on the payload for a client that needs exactness; formatting one
 * by hand would be the third place in this codebase that knows how to apply an
 * exponent, and invariant 11 exists because the exponent is 8 or 18 per stream.
 */
function priceOf(price: unknown): string | null {
  const value = (price as Record<string, unknown> | null)?.value
  return typeof value === 'string' ? formatPriceString(value) : null
}
